import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar, CartesianGrid } from "recharts";

const CHART_COLORS = ["#4A6147", "#849E81", "#CBA153", "#6B7A8F", "#5B8266", "#B85C47"];

function fmtUSD(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n));
}

function MetricCard({ label, value, sub, testid }) {
  return (
    <div className="wv-card wv-hover-lift p-6" data-testid={testid}>
      <div className="wv-overline">{label}</div>
      <div className="mt-3 text-3xl font-light font-mono tracking-tight text-[var(--wv-text)]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[var(--wv-text-3)]">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [month, setMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard/summary?month=${month}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [month]);

  const pie = useMemo(() => (data?.by_provider || []).slice(0, 6).map((p) => ({ name: p.provider, value: Math.max(p.ggr, 0) })), [data]);

  return (
    <AppShell
      title="Dashboard"
      subtitle="Monthly GGR overview, invoice status and provider distribution"
      actions={
        <input
          data-testid="dashboard-month"
          type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="h-10 px-3 rounded-sm bg-white border border-[var(--wv-border)] font-mono text-sm"
        />
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard testid="metric-total" label="Invoiced (Total)" value={fmtUSD(data?.invoices?.total_usd)} sub={`${data?.invoices?.count ?? 0} invoices`} />
        <MetricCard testid="metric-paid" label="Paid" value={fmtUSD(data?.invoices?.paid_usd)} />
        <MetricCard testid="metric-verified" label="Blockchain Verified" value={fmtUSD(data?.invoices?.verified_usd)} />
        <MetricCard testid="metric-pending" label="Pending" value={fmtUSD(data?.invoices?.pending_usd)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="wv-card p-6 lg:col-span-2" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="wv-overline">Last 12 months</div>
              <h3 className="text-lg font-medium mt-1" style={{ fontFamily: "Outfit" }}>GGR Trend</h3>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={data?.trend || []} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid stroke="#e6e8e3" vertical={false} />
                <XAxis dataKey="month" stroke="#8a9488" fontSize={12} />
                <YAxis stroke="#8a9488" fontSize={12} tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
                <Tooltip formatter={(v) => fmtUSD(v)} />
                <Line type="monotone" dataKey="ggr" stroke="#4A6147" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="wv-card p-6" data-testid="chart-provider-share">
          <div className="wv-overline">This month</div>
          <h3 className="text-lg font-medium mt-1" style={{ fontFamily: "Outfit" }}>Provider Share</h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {pie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtUSD(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="wv-card p-6 mt-6" data-testid="chart-by-provider">
        <div className="wv-overline">This month</div>
        <h3 className="text-lg font-medium mt-1" style={{ fontFamily: "Outfit" }}>GGR by Provider</h3>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={data?.by_provider || []} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
              <CartesianGrid stroke="#e6e8e3" vertical={false} />
              <XAxis dataKey="provider" stroke="#8a9488" fontSize={12} />
              <YAxis stroke="#8a9488" fontSize={12} tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Legend />
              <Bar dataKey="ggr" name="GGR" fill="#4A6147" />
              <Bar dataKey="provider_share" name="Provider Share" fill="#849E81" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AppShell>
  );
}
